import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiOpeningHours,
  ApiOpeningHoursCreate,
  ApiOpeningHoursUpdate,
  PaginatedResponse,
} from "@/types/api";

const OPENING_HOURS_FIELDS = [
  "id",
  "day_of_week",
  "open_time",
  "close_time",
  "last_reservation_time",
] as const satisfies readonly (keyof ApiOpeningHours)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useOpeningHours() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["opening-hours", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: OPENING_HOURS_FIELDS.join(",") });
      const res = await api
        .get(`opening-hours/?${params}`)
        .json<PaginatedResponse<ApiOpeningHours>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateOpeningHours() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (data: ApiOpeningHoursCreate) =>
      api.post("opening-hours/", { json: data }).json<ApiOpeningHours>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
  });
}

export function useUpdateOpeningHours() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiOpeningHoursUpdate }) =>
      api.patch(`opening-hours/${id}/`, { json: changes }).json<ApiOpeningHours>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
  });
}

export function useDeleteOpeningHours() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`opening-hours/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["opening-hours", organizationId, locationId] });
    },
  });
}
